import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ghApi, roomApi, roomTypeApi, floorPriorityApi, bookingApi, paymentResponsibilityApi, advanceBookingLimitApi, getTokens } from '../api/api';
import { mapGuestHouse, mapRoom, mapBooking, bookingToApi } from '../api/mappers';
import { ROOMS_SEED, BOOKINGS_SEED, GUEST_HOUSES } from '../data/mockData';

const BookingContext = createContext(null);

/* ── localStorage keys (used only for regular-user bookings) ── */
const USER_BOOKINGS_KEY = 'bhu_user_bookings';
const ROOMS_KEY         = 'bhu_portal_rooms';
const DATA_VERSION      = 'v6';
const VERSION_KEY       = 'bhu_portal_data_version';

/* Every guest-house, room-type and room dropdown in the app reads straight off
   this context, so display order is settled once here rather than at each of
   the ~20 call sites. `numeric` keeps "Room 2" ahead of "Room 10", and
   `sensitivity: 'base'` stops casing from splitting otherwise-equal names.
   Payment-responsibility options are deliberately left alone — the backend
   gives those an admin-configured `order`. */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const byName       = (a, b) => collator.compare(a?.name || '', b?.name || '');
const byRoomNumber = (a, b) => collator.compare(a?.roomNumber || '', b?.roomNumber || '');
const sortedByName = (list) => [...(list || [])].sort(byName);

function checkAndResetIfStale() {
  if (localStorage.getItem(VERSION_KEY) !== DATA_VERSION) {
    localStorage.removeItem(ROOMS_KEY);
    localStorage.removeItem(USER_BOOKINGS_KEY);
    localStorage.setItem(VERSION_KEY, DATA_VERSION);
  }
}

/* How often an admin tab asks the server whether anything changed. The badge
   derives its "stale" threshold from this, so the two can't drift apart. Don't
   go far past a minute: the heartbeat exists so one admin's check-in surfaces
   for another, and that story degrades fast. */
export const HEARTBEAT_INTERVAL_MS = 30000;

export function BookingProvider({ children }) {
  const { currentUser } = useAuth();

  /* ── State ─────────────────────────────────────────────── */
  const [guestHouses, setGuestHouses] = useState(GUEST_HOUSES);
  const [rooms,       setRooms]       = useState(() => {
    checkAndResetIfStale();
    const s = localStorage.getItem(ROOMS_KEY);
    if (s) { try { return JSON.parse(s); } catch { /* ignore */ } }
    return ROOMS_SEED;
  });
  const [bookings, setBookings] = useState(() => {
    const s = localStorage.getItem(USER_BOOKINGS_KEY);
    if (s) { try { return JSON.parse(s); } catch { /* ignore */ } }
    return BOOKINGS_SEED;
  });
  const [roomTypes,  setRoomTypes]  = useState({});  /* { [ghId]: [{ id, name, capacity }] } */
  const [floorPriorities, setFloorPriorities] = useState({});  /* { [ghId]: [{ id, floor, priority }] } */
  const [paymentResponsibilityOptions, setPaymentResponsibilityOptions] = useState([]);
  const [advanceBookingLimits, setAdvanceBookingLimits] = useState([]);
  /* Start "loading" synchronously (before the first paint) whenever we
     already know — from localStorage, no async needed — that this is an
     admin session and loadFromApi() is about to run. Otherwise the first
     render briefly shows stale seed/local data before the real fetch
     finishes, which flashes as "partial list, then full list". */
  const [apiLoading, setApiLoading] = useState(() => {
    try {
      const { access } = getTokens();
      const stored = localStorage.getItem('bhu_portal_session');
      const user = stored ? JSON.parse(stored) : null;
      return Boolean(access && user?.role === 'admin');
    } catch {
      return false;
    }
  });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  /* "a load has finished at least once" and "the last load succeeded" are
     different questions, and both get asked from inside callbacks whose dep
     array is [] — so they have to be refs, not state. */
  const hasLoadedOnceRef = useRef(false);
  const loadOkRef        = useRef(false);
  const [apiError,   setApiError]   = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  /* The pre-/expanded/ fan-out, kept as a fallback for the window where nginx
     is serving a fresh bundle but gunicorn hasn't been restarted onto the new
     backend yet. Deploy backend-first and this never runs. It also restores the
     per-endpoint failure isolation the old code had: one endpoint 403ing
     degrades to an empty list instead of failing the whole load. */
  const fanOutGuestHouses = useCallback(async () => {
    const ghList = await ghApi.list();
    const perGh  = await Promise.all(ghList.map(gh => Promise.all([
      roomApi.list(gh.id).catch(() => []),
      roomTypeApi.list(gh.id).catch(() => []),
      floorPriorityApi.list(gh.id).catch(() => []),
    ])));
    return ghList.map((gh, idx) => ({
      ...gh,
      rooms:            perGh[idx][0],
      room_types:       perGh[idx][1],
      floor_priorities: perGh[idx][2],
    }));
  }, []);

  /* ── Load everything from the API (admin only) ─────────── */
  const loadFromApi = useCallback(async () => {
    const { access } = getTokens();
    if (!access) return;

    setApiLoading(true);
    setApiError(null);
    /* Drops the seed rooms once a real token appears — which only matters on
       the first load. Doing it on every refresh empties the Room Map and
       Rooms tab mid-render each time the heartbeat sees a change. */
    if (!hasLoadedOnceRef.current) setRooms([]);

    try {
      /* 1 – Everything the dashboard needs to draw itself, in three requests.
             ghApi.expanded() returns each guest house with its rooms, room
             types and floor priorities nested; it used to be one request for
             the guest-house list plus three more per house, so six houses cost
             nineteen round trips on every load. */
      const [ghPayload, apiBookings, apiPaymentOptions, apiAdvanceLimits] = await Promise.all([
        ghApi.expanded().catch(fanOutGuestHouses),
        bookingApi.list().catch(() => []),
        paymentResponsibilityApi.list().catch(() => []),
        advanceBookingLimitApi.list().catch(() => []),
      ]);
      setPaymentResponsibilityOptions(apiPaymentOptions || []);
      setAdvanceBookingLimits(apiAdvanceLimits || []);

      /* mapGuestHouse and mapRoom both expect a raw API guest house, so strip
         the nested children back off rather than handing them the fat objects. */
      const ghList = ghPayload.map(({ rooms: _r, room_types: _t, floor_priorities: _f, ...gh }) => gh);
      setGuestHouses(sortedByName(ghList.map(mapGuestHouse)));

      /* Build room-type lookup { typeId → capacity }, keyed by guest house id
         rather than by position in the list. */
      const typeCapMap = {};
      const rtByGh     = {};
      const fpByGh     = {};
      ghPayload.forEach(gh => {
        const types = gh.room_types || [];
        rtByGh[gh.id] = sortedByName(types);      /* raw — RoomTypesTab and RoomMapTab read .attributes */
        fpByGh[gh.id] = gh.floor_priorities || [];  /* raw, already ordered by priority */
        types.forEach(t => { typeCapMap[t.id] = t.capacity; });
      });
      setRoomTypes(rtByGh);
      setFloorPriorities(fpByGh);

      /* A second pass, deliberately: a room's capacity comes from typeCapMap,
         which isn't complete until every guest house's types are in. Folding
         this into the loop above gives every room whose type belongs to a later
         guest house a null capacity. */
      const allRooms = ghPayload.flatMap(gh =>
        (gh.rooms || []).map(r => mapRoom(r, ghList, typeCapMap[r.room_type] ?? null))
      );
      setRooms([...allRooms].sort(byRoomNumber));

      /* Map bookings */
      setBookings((apiBookings || []).map(b => mapBooking(b, apiPaymentOptions || [])));
      setApiError(null);
      setLastSyncedAt(new Date().toISOString());
      loadOkRef.current = true;
      return true;
    } catch (e) {
      console.error('[BookingContext] API load failed:', e.message);
      setApiError(e.message || 'Failed to load data from server.');
      loadOkRef.current = false;
      return false;
    } finally {
      setApiLoading(false);
      setHasLoadedOnce(true);
      hasLoadedOnceRef.current = true;
    }
  }, [fanOutGuestHouses]);

  /* ── Reload when admin logs in, reset when logged out ───── */
  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadFromApi();
    } else if (!currentUser) {
      /* Logged out — restore local data */
      checkAndResetIfStale();
      const s = localStorage.getItem(ROOMS_KEY);
      setRooms(s ? JSON.parse(s) : ROOMS_SEED);
      const b = localStorage.getItem(USER_BOOKINGS_KEY);
      setBookings(b ? JSON.parse(b) : BOOKINGS_SEED);
      setGuestHouses(GUEST_HOUSES);
      setRoomTypes({});
      setFloorPriorities({});
      setPaymentResponsibilityOptions([]);
      setAdvanceBookingLimits([]);
      hasLoadedOnceRef.current = false;
      loadOkRef.current = false;
    }
  }, [currentUser, loadFromApi]);

  /* Regular ("user") sessions never go through loadFromApi (that's admin-only),
     but createBooking still needs option labels to display the payment
     responsibility it just submitted — fetch this cheap public list for any
     logged-in session, not just admins. */
  useEffect(() => {
    if (!currentUser) return;
    paymentResponsibilityApi.list().then(setPaymentResponsibilityOptions).catch(() => {});
  }, [currentUser]);

  /* ── Persist local user bookings to localStorage ─────────── */
  useEffect(() => {
    if (currentUser?.role !== 'admin') {
      localStorage.setItem(USER_BOOKINGS_KEY, JSON.stringify(bookings));
    }
  }, [bookings, currentUser]);

  /* ── Visibility-aware heartbeat polling ────────────────────
     Replaces the old tab-gated 30s interval that only ever refreshed
     while sitting on the "bookings" tab. This runs for every admin tab so
     one gh-admin's check-in/checkout shows up for another admin looking at
     Front Desk / Room Map / Bookings / Summary without a manual refresh.
     Polls a cheap heartbeat first and only pays for a full reload when it
     actually changed; pauses entirely while the browser tab isn't visible. */
  /* null until the first poll primes it — see the first-poll branch below. */
  const lastHeartbeatRef = useRef(null);
  const pollInFlightRef  = useRef(false);
  useEffect(() => {
    if (currentUser?.role !== 'admin') return undefined;
    let interval;
    const poll = async () => {
      /* setInterval doesn't wait for the previous tick. A slow reload used to
         let ticks stack up behind it and fire again the moment it landed. */
      if (pollInFlightRef.current || document.hidden) return;
      pollInFlightRef.current = true;
      try {
        const hb  = await bookingApi.heartbeat();
        /* `unseen` changes when another admin reads an indent, which leaves updated_at alone. */
        const sig = `${hb.last_updated}|${hb.count}|${hb.unseen}`;

        if (lastHeartbeatRef.current === null && loadOkRef.current) {
          /* First poll of the session. The load that ran on mount already holds
             exactly this data. The ref used to start as {null, null}, so this
             tick always looked like a change and bought a second full reload a
             few seconds into every page load — record the signature, fetch
             nothing. */
          lastHeartbeatRef.current = sig;
          setLastSyncedAt(new Date().toISOString());
        } else if (sig !== lastHeartbeatRef.current || !loadOkRef.current) {
          /* !loadOkRef also retries a load that failed; previously only the
             first-poll bug provided that, and only by accident. */
          if (await loadFromApi()) lastHeartbeatRef.current = sig;
          /* ...and only on success. Advancing the ref before awaiting let a
             failed reload convince the next tick we were already in sync, so
             the UI stayed stale until the server changed again. */
        } else {
          /* Nothing changed, but the heartbeat still confirms we're in sync —
             without this the badge goes "stale" after a spell of server-side
             inactivity even though polling is working fine. */
          setLastSyncedAt(new Date().toISOString());
        }
      } catch {
        /* heartbeat failures are non-fatal — just skip this cycle */
      } finally {
        pollInFlightRef.current = false;
      }
    };
    const start = () => { if (!interval) interval = setInterval(poll, HEARTBEAT_INTERVAL_MS); };
    const stop = () => { clearInterval(interval); interval = null; };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { poll(); start(); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    /* Prime immediately rather than one interval from now: a booking changed
       between the mount-time load finishing and that first tick would otherwise
       be baked into the primed signature and never noticed. Costs one cheap
       aggregate query. */
    if (!document.hidden) { poll(); start(); }
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUser, loadFromApi]);

  /* ─────────────────────────────────────────────────────────
     BOOKING OPERATIONS
  ───────────────────────────────────────────────────────── */

  /* Create booking — always goes to the real API (X-API-Key). No local fallback:
     if the API call fails, the error must surface to the form so the user knows
     the booking was NOT actually submitted. */
  const createBooking = async (data) => {
    const apiPayload = bookingToApi(data, guestHouses);
    const file       = data.officialAttachment instanceof File ? data.officialAttachment : null;

    let requestBody;
    if (file) {
      const fd = new FormData();
      Object.entries(apiPayload).forEach(([key, value]) => {
        if (key === 'preferences') {
          fd.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined && value !== '') {
          fd.append(key, String(value));
        }
      });
      fd.append('official_attachment', file, file.name);
      requestBody = fd;
    } else {
      requestBody = JSON.stringify(apiPayload);
    }

    const created = await bookingApi.create(requestBody);
    const nb = { ...mapBooking(created, paymentResponsibilityOptions), userId: currentUser?.id };
    setBookings(prev => [nb, ...prev]);
    return nb;
  };

  /* Confirm (approve) a booking — admin action. room_ids may span any
     guest house the admin is authorized for; the backend assigns each
     room to its own guest house, so no transfer step is needed here. */
  const confirmBooking = async (bookingId, roomIds, remarks = '', roomWindows) => {
    const updated = await bookingApi.confirm(bookingId, roomIds, remarks, roomWindows);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return { ...mapped, mailAutoSent: updated.mail_auto_sent };
  };

  /* Partially confirm a booking — admin action */
  const partialConfirmBooking = async (bookingId, roomIds, note = '') => {
    const updated = await bookingApi.partialConfirm(bookingId, roomIds, note);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Add extra room(s) to an already-confirmed/checked-in booking — admin
     action. Named `addRoomToBooking` (not `addRoom`, which already means
     "create a new physical room" elsewhere in this context) — room_ids may
     span any guest house the admin is authorized for. */
  const addRoomToBooking = async (bookingId, roomIds, roomWindows) => {
    const updated = await bookingApi.addRoom(bookingId, roomIds, roomWindows);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Reject a booking — admin action */
  const rejectBooking = async (bookingId, remarks = '') => {
    const updated = await bookingApi.reject(bookingId, remarks);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return { ...mapped, mailAutoSent: updated.mail_auto_sent };
  };

  /* Cancel a booking — admin or user */
  const cancelBooking = async (bookingId, remarks = '') => {
    try {
      const updated = await bookingApi.cancel(bookingId, remarks);
      const mapped  = mapBooking(updated, paymentResponsibilityOptions);
      setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
      return { ...mapped, mailAutoSent: updated.mail_auto_sent };
    } catch {
      /* Fallback: update locally */
      setBookings(prev =>
        prev.map(b => b.id === bookingId ? { ...b, status: 'Cancelled' } : b)
      );
    }
  };

  /* Transfer booking — admin action */
  const transferBooking = async (bookingId, toGhId, remarks = '') => {
    const updated = await bookingApi.transfer(bookingId, toGhId, remarks);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Reschedule (prepone/postpone) — admin action, pre-checkin bookings only */
  const rescheduleBooking = async (bookingId, payload) => {
    const updated = await bookingApi.reschedule(bookingId, payload);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Edit applicant/guest details post-creation — admin action */
  const editBookingDetails = async (bookingId, payload) => {
    const updated = await bookingApi.editDetails(bookingId, payload);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Raw single-field edit — has_edit_access admins only. Same shape as
     editBookingDetails; the backend allowlists the field and audits the diff. */
  const adminEditBookingField = async (bookingId, payload) => {
    const updated = await bookingApi.fieldEdit(bookingId, payload);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Raw edit of one room assignment's check-in / check-out timestamp —
     has_edit_access admins only. `payload` is { room_id, actual_checkin?,
     actual_checkout? }. */
  const adminEditRoomField = async (bookingId, payload) => {
    const updated = await bookingApi.roomFieldEdit(bookingId, payload);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Per-room front-desk actions — admin action. Mirror FrontDeskModal's
     existing bookingApi calls so BookingDetailModal can offer the same
     actions without leaving the detail view. */
  const checkinBooking = async (bookingId, assignmentIds, noShow = false) => {
    const updated = await bookingApi.checkin(bookingId, assignmentIds, noShow);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  const checkoutBooking = async (bookingId, assignmentIds) => {
    const updated = await bookingApi.checkout(bookingId, assignmentIds);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  const extendBooking = async (bookingId, newDepartureDate, assignmentIds, newDepartureTime, arrival) => {
    const updated = await bookingApi.extend(bookingId, newDepartureDate, assignmentIds, newDepartureTime, arrival);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  const changeRoomBooking = async (bookingId, assignmentId, newRoomId, oldRoomId) => {
    const updated = await bookingApi.changeRoom(bookingId, assignmentId, newRoomId, oldRoomId);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  const removeRoomFromBooking = async (bookingId, assignmentId, remarks) => {
    const updated = await bookingApi.removeRoom(bookingId, assignmentId, remarks);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  const setRoomGuestName = async (bookingId, assignmentId, guestName) => {
    const updated = await bookingApi.setRoomGuestName(bookingId, assignmentId, guestName);
    const mapped  = mapBooking(updated, paymentResponsibilityOptions);
    setBookings(prev => prev.map(b => b.id === bookingId ? mapped : b));
    return mapped;
  };

  /* Merge one already-mapped booking into context state — used by action
     modals (e.g. FrontDeskModal) for an instant local update instead of
     waiting on the next poll cycle. */
  const updateBookingLocal = (mapped) => {
    setBookings(prev => prev.map(b => b.id === mapped.id ? mapped : b));
  };

  /* Opening an indent marks it read for everyone. The row un-bolds at once; a
     failed call is left for the next reload to correct. */
  const markBookingSeen = (booking) => {
    if (!booking?.isUnread) return;
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, isUnread: false } : b));
    bookingApi.markSeen(booking.id).catch(() => {});
  };

  /* Legacy helpers kept for backward compatibility */
  const updateBookingStatus = (bookingId, status) =>
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));

  const updateBooking = (bookingId, updates) =>
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, ...updates } : b));

  /* ─────────────────────────────────────────────────────────
     ROOM OPERATIONS
  ───────────────────────────────────────────────────────── */

  /* ─────────────────────────────────────────────────────────
     GUEST HOUSE OPERATIONS
  ───────────────────────────────────────────────────────── */

  const addGuestHouse = async (data) => {
    const created = await ghApi.create({
      name:               data.name,
      short_name:         (data.shortName || '').trim(),
      address:            data.address || '',
      contact_email:      data.contactEmail || '',
      contact_phone:      data.contactPhone || '',
      is_active:          data.isActive !== false,
      geo_location_link:  data.geoLocationLink || '',
    });
    const mapped = mapGuestHouse(created);
    setGuestHouses(prev => sortedByName([...prev, mapped]));
    return mapped;
  };

  const updateGuestHouse = async (ghId, data) => {
    const updated = await ghApi.update(ghId, {
      name:               data.name,
      short_name:         (data.shortName || '').trim(),
      address:            data.address || '',
      contact_email:      data.contactEmail || '',
      contact_phone:      data.contactPhone || '',
      is_active:          data.isActive !== false,
      geo_location_link:  data.geoLocationLink || '',
    });
    const mapped = mapGuestHouse(updated);
    setGuestHouses(prev => sortedByName(prev.map(g => g.id === ghId ? mapped : g)));
    return mapped;
  };

  const deleteGuestHouse = async (ghId) => {
    await ghApi.delete(ghId);
    setGuestHouses(prev => prev.filter(g => g.id !== ghId));
  };

  const minutesOr = (value, fallback) => {
    const n = value === '' || value == null ? NaN : Number(value);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };

  /* Narrower sibling of updateGuestHouse — PATCHes just the booking-window
     and auto-booking fields, so a GH_ADMIN can manage these from a tab that
     doesn't show (or let them edit) the identity fields. */
  const updateGuestHouseBookingSettings = async (ghId, data) => {
    const updated = await ghApi.update(ghId, {
      auto_book_enabled:        data.autoBookEnabled ?? false,
      auto_book_max_rooms:      Number(data.autoBookMaxRooms) || 2,
      auto_book_min_lead_days:  Number(data.autoBookMinLeadDays) || 3,
      auto_book_lookahead_days: Number(data.autoBookLookaheadDays) || 10,
      auto_book_max_stay_nights:
        data.autoBookMaxStayNights !== '' && data.autoBookMaxStayNights != null
          ? Number(data.autoBookMaxStayNights) : null,
      /* 0 is a real setting here (no gap, no grace), so only a blank or
         non-numeric value falls back — `|| 60` silently saved 0 as 60. */
      room_turnover_buffer_minutes: minutesOr(data.roomTurnoverBufferMinutes, 60),
      late_checkout_grace_minutes:  minutesOr(data.lateCheckoutGraceMinutes, 30),
      auto_release_maintenance:     !!data.autoReleaseMaintenance,
      allow_room_reuse:             !!data.allowRoomReuse,
    });
    const mapped = mapGuestHouse(updated);
    setGuestHouses(prev => sortedByName(prev.map(g => g.id === ghId ? mapped : g)));
    return mapped;
  };

  /* ─────────────────────────────────────────────────────────
     FLOOR PRIORITY OPERATIONS
  ───────────────────────────────────────────────────────── */

  const addFloorPriority = async (ghId, data) => {
    const created = await floorPriorityApi.create(Number(ghId), {
      floor: data.floor,
      priority: Number(data.priority),
    });
    setFloorPriorities(prev => ({
      ...prev,
      [ghId]: [...(prev[ghId] || []), created],
    }));
    return created;
  };

  const updateFloorPriority = async (ghId, id, data) => {
    const updated = await floorPriorityApi.update(Number(ghId), id, {
      floor: data.floor,
      priority: Number(data.priority),
    });
    setFloorPriorities(prev => ({
      ...prev,
      [ghId]: (prev[ghId] || []).map(f => f.id === id ? updated : f),
    }));
    return updated;
  };

  const deleteFloorPriority = async (ghId, id) => {
    await floorPriorityApi.delete(Number(ghId), id);
    setFloorPriorities(prev => ({
      ...prev,
      [ghId]: (prev[ghId] || []).filter(f => f.id !== id),
    }));
  };

  /* ─────────────────────────────────────────────────────────
     PAYMENT RESPONSIBILITY OPTION OPERATIONS
  ───────────────────────────────────────────────────────── */

  const addPaymentResponsibilityOption = async (data) => {
    const created = await paymentResponsibilityApi.create({
      booking_type: data.bookingType,
      code:         data.code,
      label:        data.label,
      is_active:    data.isActive !== false,
      order:        Number(data.order) || 0,
    });
    setPaymentResponsibilityOptions(prev => [...prev, created]);
    return created;
  };

  const updatePaymentResponsibilityOption = async (id, data) => {
    const updated = await paymentResponsibilityApi.update(id, {
      booking_type: data.bookingType,
      code:         data.code,
      label:        data.label,
      is_active:    data.isActive !== false,
      order:        Number(data.order) || 0,
    });
    setPaymentResponsibilityOptions(prev => prev.map(o => o.id === id ? updated : o));
    return updated;
  };

  const deletePaymentResponsibilityOption = async (id) => {
    await paymentResponsibilityApi.delete(id);
    setPaymentResponsibilityOptions(prev => prev.filter(o => o.id !== id));
  };

  /* ─────────────────────────────────────────────────────────
     ADVANCE BOOKING LIMIT OPERATIONS
     The 4 (booking_type, actor_role) rows are seeded server-side -- this
     only ever edits max_days on an existing row, never adds or removes one.
  ───────────────────────────────────────────────────────── */

  const updateAdvanceBookingLimit = async (id, maxDays) => {
    const updated = await advanceBookingLimitApi.update(id, {
      max_days: maxDays === '' || maxDays == null ? null : Number(maxDays),
    });
    setAdvanceBookingLimits(prev => prev.map(l => l.id === id ? updated : l));
    return updated;
  };

  /* Resolve room type ID: check loaded room-types, then existing rooms, then create */
  async function resolveRoomTypeId(ghId, typeName, capacity = 1) {
    /* 1. from the room-types list we loaded */
    const ghTypes = roomTypes[ghId] || [];
    const found   = ghTypes.find(t => t.name === typeName);
    if (found) return found.id;

    /* 2. derive from an already-loaded room that has the same type */
    const existingRoom = rooms.find(r => r.guestHouseId === ghId && r.roomType === typeName && r.roomTypeId);
    if (existingRoom) return existingRoom.roomTypeId;

    /* 3. create the room type on the fly */
    const created = await roomTypeApi.create(ghId, {
      name:        typeName,
      capacity,
      description: '',
      attributes:  {},
    });
    setRoomTypes(prev => ({
      ...prev,
      [ghId]: sortedByName([...(prev[ghId] || []), created]),
    }));
    return created.id;
  }

  const addRoom = async (roomData) => {
    const ghId      = Number(roomData.guestHouseId);
    const typeId    = await resolveRoomTypeId(ghId, roomData.roomType, roomData.capacity);
    const apiResult = await roomApi.create(ghId, {
      room_type:          typeId,
      room_number:        roomData.roomNumber,
      floor:              roomData.floor || '1',
      is_active:          true,
      status:             roomData.status === 'maintenance' ? 'MAINTENANCE' : 'AVAILABLE',
      maintenance_remark: roomData.status === 'maintenance' ? (roomData.maintenanceRemark || '') : '',
    });
    const newRoom = mapRoom(apiResult, guestHouses, roomData.capacity);
    setRooms(prev => [...prev, newRoom].sort(byRoomNumber));
    return newRoom;
  };

  /* Room "status" (Available/Maintenance) is a distinct concept from
     is_active (true soft-delete) — routed through its own set-status
     endpoint so the change is audited server-side, not folded into the
     generic PATCH below. */
  const setRoomStatus = async (roomId, statusValue, remark = '') => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    const updated = await roomApi.setStatus(
      room.guestHouseId, roomId, statusValue === 'maintenance' ? 'MAINTENANCE' : 'AVAILABLE', remark,
    );
    const mapped = mapRoom(updated, guestHouses, room.capacity);
    setRooms(prev => prev.map(r => r.id === roomId ? { ...r, ...mapped } : r).sort(byRoomNumber));
    return mapped;
  };

  const updateRoom = async (roomId, updates) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    const ghId    = room.guestHouseId;
    const apiUpd  = {};

    if (updates.roomNumber !== undefined)
      apiUpd.room_number = updates.roomNumber;
    if (updates.floor !== undefined)
      apiUpd.floor = updates.floor;
    if (updates.roomType !== undefined) {
      apiUpd.room_type = await resolveRoomTypeId(
        ghId, updates.roomType, updates.capacity ?? room.capacity
      );
    }

    if (Object.keys(apiUpd).length > 0) {
      await roomApi.update(ghId, roomId, apiUpd);
    }
    if (updates.status !== undefined) {
      await setRoomStatus(roomId, updates.status, updates.maintenanceRemark);
    }
    setRooms(prev => prev.map(r => r.id === roomId ? { ...r, ...updates } : r));
  };

  const deleteRoom = async (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    await roomApi.delete(room.guestHouseId, roomId);
    setRooms(prev => prev.filter(r => r.id !== roomId));
  };

  /* ─────────────────────────────────────────────────────────
     ROOM TYPE OPERATIONS
  ───────────────────────────────────────────────────────── */

  const addRoomType = async (ghId, data) => {
    const created = await roomTypeApi.create(Number(ghId), {
      name:        data.name,
      short_name:  (data.shortName || '').trim(),
      capacity:    Number(data.capacity) || 1,
      description: data.description || '',
      attributes:  { icon: data.icon || 'bed', extraIcon: data.extraIcon || null },
      base_price:  data.basePrice !== '' && data.basePrice != null ? Number(data.basePrice) : null,
      gst_applicable: data.gstApplicable !== false,
      auto_book_threshold_percent:
        data.autoBookThreshold !== '' && data.autoBookThreshold != null
          ? Number(data.autoBookThreshold) : null,
    });
    setRoomTypes(prev => ({
      ...prev,
      [ghId]: sortedByName([...(prev[ghId] || []), created]),
    }));
    return created;
  };

  const updateRoomType = async (ghId, roomTypeId, data) => {
    const updated = await roomTypeApi.update(Number(ghId), roomTypeId, {
      name:        data.name,
      short_name:  (data.shortName || '').trim(),
      capacity:    Number(data.capacity) || 1,
      description: data.description || '',
      attributes:  { icon: data.icon || 'bed', extraIcon: data.extraIcon || null },
      base_price:  data.basePrice !== '' && data.basePrice != null ? Number(data.basePrice) : null,
      gst_applicable: data.gstApplicable !== false,
      auto_book_threshold_percent:
        data.autoBookThreshold !== '' && data.autoBookThreshold != null
          ? Number(data.autoBookThreshold) : null,
    });
    setRoomTypes(prev => ({
      ...prev,
      [ghId]: sortedByName((prev[ghId] || []).map(t => t.id === roomTypeId ? updated : t)),
    }));
    return updated;
  };

  const deleteRoomType = async (ghId, roomTypeId) => {
    await roomTypeApi.delete(Number(ghId), roomTypeId);
    setRoomTypes(prev => ({
      ...prev,
      [ghId]: (prev[ghId] || []).filter(t => t.id !== roomTypeId),
    }));
  };

  /* Re-fetch room types straight from the API for one guest house (or all,
     when ghId is omitted) — picks up types added directly on the backend. */
  const refreshRoomTypes = async (ghId = null) => {
    const targets = ghId ? guestHouses.filter(g => g.id === Number(ghId)) : guestHouses;
    const entries = await Promise.all(
      targets.map(gh => roomTypeApi.list(gh.id).then(types => [gh.id, types]).catch(() => [gh.id, []]))
    );
    setRoomTypes(prev => {
      const next = { ...prev };
      entries.forEach(([id, types]) => { next[id] = sortedByName(types); });
      return next;
    });
  };

  return (
    <BookingContext.Provider value={{
      /* data */
      rooms, bookings, guestHouses, roomTypes, floorPriorities, paymentResponsibilityOptions,
      advanceBookingLimits,
      /* status */
      apiLoading, apiError, hasLoadedOnce, lastSyncedAt,
      /* booking ops */
      createBooking,
      confirmBooking, partialConfirmBooking, addRoomToBooking,
      rejectBooking,  cancelBooking, transferBooking,
      rescheduleBooking, editBookingDetails, adminEditBookingField, adminEditRoomField,
      checkinBooking, checkoutBooking, extendBooking, changeRoomBooking, removeRoomFromBooking, setRoomGuestName,
      updateBookingLocal, markBookingSeen,
      /* legacy compat */
      updateBookingStatus, updateBooking,
      /* guest house ops */
      addGuestHouse, updateGuestHouse, deleteGuestHouse, updateGuestHouseBookingSettings,
      /* floor priority ops */
      addFloorPriority, updateFloorPriority, deleteFloorPriority,
      /* payment responsibility option ops */
      addPaymentResponsibilityOption, updatePaymentResponsibilityOption, deletePaymentResponsibilityOption,
      /* advance booking limit ops */
      updateAdvanceBookingLimit,
      /* room ops */
      addRoom, updateRoom, setRoomStatus, deleteRoom,
      /* room type ops */
      addRoomType, updateRoomType, refreshRoomTypes, deleteRoomType,
      /* manual reload */
      reloadFromApi: loadFromApi,
    }}>
      {children}
    </BookingContext.Provider>
  );
}

export const useBooking = () => useContext(BookingContext);
